/**
 * Every sentence the V1 to V2 migration shows, in one place so the house
 * rules (plain sentences, no en or em dashes, never a raw error) are checked
 * by one test. The step labels live with the other operations' labels in
 * `features/custodial/operationPhase.ts`.
 */
export const MIGRATION_COPY = {
    buttonLabel: 'Migrate',
    buttonTitle: 'Move your V1 balance to your Snappy Wallet',
    title: 'Move your balance to your Snappy Wallet',
    description: 'Your V1 balance moves to the Snappy Wallet you use in V2.',
    amountLabel: 'Amount',
    amountLoading: 'Reading your V1 balance',
    amountUnavailable:
        'We could not read your V1 balance right now. Try again in a minute.',
    reserveNote: 'About 1.01 USDC stays on HyperCore as the account reserve.',
    nothingToMigrate: 'There is nothing to migrate right now.',
    destinationLabel: 'To your Snappy Wallet',
    noDestination:
        'Set up your Snappy Wallet in V2 first, then come back here.',
    whatHappensTitle: 'What happens',
    whatHappens: [
        'Your funds come back from HyperCore to your wallet.',
        'They are sent to your Snappy Wallet on HyperEVM.',
        'In V2, you bridge them into your trading account.',
    ],
    blocked: 'Close your open positions and orders first.',
    needsWallet: 'Migrating needs the wallet that owns this account connected.',
    connectWallet: 'Connect wallet',
    start: 'Start migration',
    signing: 'Sign the migration in your wallet',
    running: 'Moving your balance. Keep this window open.',
    done: (amount: string, address: string) =>
        `Migrated $${amount} to ${address} on HyperEVM.`,
    doneNoAmount: (address: string) =>
        `Migrated your balance to ${address} on HyperEVM.`,
    doneNext:
        'In V2, open Deposit and tap Bridge to Hyperliquid to move it into your trading account.',
    gasMissing:
        'We could not add HYPE for gas this time, so you may need a little HYPE in your Snappy Wallet to bridge it.',
    switchToV2: 'Switch to V2',
    close: 'Close',
    failedTitle: 'The migration did not finish',
    failedSafety: 'Whatever did not move is still in your V1 balance.',
    retry: 'Retry',
    jobFailed: 'The migration could not be completed.',
    deadline:
        'The migration is taking longer than expected. It may still finish, so check your Snappy Wallet in a few minutes.',
    cancelled: 'You cancelled the signature, so nothing was moved.',
    wrongWallet: 'Connect the wallet that owns this account, then try again.',
    signIn: 'Sign in with the wallet that owns this account, then try again.',
} as const;

/**
 * The refusals `POST /speed/migrate` can answer with (plan §3.2), in words
 * that fit a migration. Anything else goes through the shared presenter.
 */
export const MIGRATION_ERROR_COPY: Readonly<Record<string, string>> = {
    migrationDestinationUnknown:
        'That Snappy Wallet is not one of the wallets backed up on your account. Open V2, check your Snappy Wallet, then try again.',
    migrationNeedsSocialSession:
        'Your social sign in has expired. Sign in again, then retry the migration.',
    activeTradeExists: MIGRATION_COPY.blocked,
    activePositionExists: MIGRATION_COPY.blocked,
    positionStateUnavailable:
        'We could not check your open positions right now. Try again in a minute.',
    withdrawInFlight:
        'A withdrawal or migration is already running. Wait for it to finish, then try again.',
    // the codes the API actually answers for an in-flight or parked withdrawal
    activeWithdrawExists:
        'A withdrawal or migration is already running. Wait for it to finish, then try again.',
    withdrawRecoveryPending:
        'A previous withdrawal still needs attention. Finish it in the Quick trade wallet panel, then try again.',
    insufficientBalance: 'There is not enough balance to migrate right now.',
    custodialWalletMissing:
        'Your V1 wallet is not set up, so there is nothing to migrate.',
};
